import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import Inputmask from "inputmask";
import { Eye, EyeOff, Loader, Lock, Save, X } from "lucide-react";
import Spacing from "../Spacing";
import DropdownWithSearch from "../DropdownWithSearch";
import { can_visit, getPractice, usPhoneFormat } from "../../helpers";
import { useSelector } from "react-redux";
import { Drawer } from 'vaul';

export default function EditUserInfoModal({
  allProviders = [],
  onClose = () => { },
  userData = {},
  handleDelete = () => { },
  handleUpdatedInvitedUserInfo = () => { },
  userRoles=[],
  updateLoader = false,
}) {
  const userProfileData = useSelector((state) => state?.practice?.userProfile);

  const [invitedUserData, setInvitedUserData] = useState({
    recipient_email: "",
    first_name: "",
    last_name: "",
    contact: "",
    roles: [],
    groups: [],
    speciality_ids: [],
    is_provider: false,
    ehr_type: "",
    ehr_username: "",
    ehr_password: "",
    linked_providers: [],
    emr_provider_id: "",
    default_location_id: ""
  });

  const [allSpeciality, setAllSpeciality] = useState([]);
  const dropdownRef = useRef(null);
  const [ehrPassword, setEhrPassword] = useState(false);
  const [allProvidersOptions, setAllProvidersOptions] = useState([]);
  const [ehrType] = useState([
    { value: 'ECW', label: 'ECW', },
    { value: 'MDLand', label: 'MDLand', },
    { value: 'VeradigmEHR', label: 'VeradigmEHR', },
    { value: 'AthenaHealth', label: 'AthenaHealth', }
  ]);

  const getAllSpeciality = async () => {
    try {
      const response = getPractice("specialities");
      if (response?.length) {
        const allSpecialities = response?.map((option) => ({
          value: option?.id,
          label: option?.name,
        }))
        setAllSpeciality(allSpecialities);
      };
    } catch (error) {
      // console.log("🚀 ~ getAllSpeciality: ~ error:", error);
    }
  };

  useEffect(() => {
    getAllSpeciality();
  }, []);

  const getAllProviders = () => {
    try {     

      if (allProviders?.length) {
        const allProvidersFormatted = allProviders?.map((option) => ({
          value: option?.provider_id,
          label: `${option?.first_name} ${option?.last_name}`,
        }))
        setAllProvidersOptions(allProvidersFormatted);
      };
    } catch (error) {
      // console.log("🚀 ~ getAllProviders: ~ error:", error);
    }
  };

  useEffect(() => {
    getAllProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLinkedProvidersChange = (selectedOption) => {
    const selectedValues = selectedOption ? selectedOption?.map((option) => option?.value) : [];
    setInvitedUserData({
      ...invitedUserData,
      linked_providers: selectedValues,
    });
  };

  const handleSpecialityChange = (selectedOption) => {
    const selectedValues = selectedOption ? selectedOption?.map((option) => option?.value) : [];

    setInvitedUserData({
      ...invitedUserData,
      speciality_ids: selectedValues,
    });
  };


  const handleChangeRoles = (value) => {
    setInvitedUserData({
      ...invitedUserData,
      roles: invitedUserData?.roles?.includes(value)
        ? invitedUserData?.roles?.filter((role) => role !== value)
        : [...invitedUserData?.roles, value],
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "contact") {
      const phoneInputs = document?.querySelectorAll(".us_phone");
      Inputmask({
        mask: "(999) 999-9999",
        showMaskOnHover: false,
        showMaskOnFocus: true,
      }).mask(phoneInputs);
    }
    setInvitedUserData({
      ...invitedUserData,
      [name]: value
    });
  };

  useEffect(() => {
    setInvitedUserData({
      user_id: userData.id,
      contact: userData.contact || "",
      first_name: userData.first_name,
      last_name: userData.last_name,
      roles: userData?.roles?.filter(role => userRoles?.includes(role)),
      recipient_email: userData.email,
      speciality_ids: userData?.speciality_ids || [],
      groups: userData?.groups || [],
      is_provider: userData?.is_provider || false,
      ehr_type: userData?.ehr_type || "",
      ehr_username: userData?.ehr_username || "",
      linked_providers: userData?.linked_providers || [],
      emr_provider_id: userData?.emr_provider_id || "",
      default_location_id: userData?.default_location_id || ""
    });
  }, [userData, userRoles]);

  const handleSelectGroup = (account) => {
    const accountKey = `/${account?.practices[0]?.account_name}`;
    const practiceKeys = account?.practices?.map((practice) => `/${account?.practices[0]?.account_name}/${practice?.name}`);
  
    setInvitedUserData((prevData) => {
      const isAccountSelected = prevData?.groups?.includes(accountKey);

      if (isAccountSelected) {
        const nextGroups = prevData?.groups?.filter(
          (group) => group !== accountKey && !practiceKeys?.includes(group)
        );

        const remainingLocationIds = new Set();
        (userProfileData?.accounts || []).forEach((acc) => {
          (acc?.practices || []).forEach((p) => {
            const key = `/${acc?.practices[0]?.account_name}/${p?.name}`;
            if (nextGroups.includes(key)) remainingLocationIds.add(String(p?.location_id));
          });
        });

        const keepDefault = remainingLocationIds.has(String(prevData?.default_location_id)) ? prevData?.default_location_id : "";
        return {
          ...prevData,
          groups: nextGroups,
          default_location_id: keepDefault
        };
      }

      return {
        ...prevData,
        groups: [...new Set([...prevData.groups || [], accountKey, ...practiceKeys])],
      };
    });
  };

  const handleEhrPasswordChange = (e) => {
    const { name, value } = e.target;
    setInvitedUserData({
      ...invitedUserData,
      [name]: value
    });
  };
  
  const handleChangePractices = (practiceName, accountName, location_id) => {
    const practiceKey = `/${accountName}/${practiceName}`;
    const accountKey = `/${accountName}`;
  
    setInvitedUserData((prevData) => {
      const isPracticeSelected = prevData?.groups?.includes(practiceKey);

      if (isPracticeSelected) {
        const updatedGroups = prevData?.groups?.filter((group) => group !== practiceKey);

        const hasRemainingPractices = updatedGroups?.some((group) =>
          group?.startsWith(`${accountKey}/`)
        );
        const nextDefault = String(prevData?.default_location_id) === String(location_id) ? "" : prevData?.default_location_id;
  
        return {
          ...prevData,
          groups: hasRemainingPractices
            ? updatedGroups
            : updatedGroups?.filter((group) => group !== accountKey),
          default_location_id: nextDefault
        };
      }

      return {
        ...prevData,
        groups: [...new Set([...(prevData.groups || []), practiceKey, accountKey])],
      };
    });
  };

  const handleIsProviderChange = async (e) => {
    const value = !invitedUserData?.is_provider;
    setInvitedUserData({ ...invitedUserData, is_provider: value });
  };

  const updatePracticeHTML = () => {
    let seletedValues= invitedUserData?.groups;
    let multiple_select = document.querySelector( ".multiple_select .docpro_dropdown_with_search_btn" );
    if (multiple_select) {
      if (seletedValues?.length === 0) {
        multiple_select.innerHTML = 'Select Practices <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-down "><path d="m6 9 6 6 6-6"></path></svg>';
      } else {
        seletedValues.sort();
        let htmlString = seletedValues
          .map((item) => `<span>${item}</span>`)
          .join(", ");
        multiple_select.innerHTML = htmlString;
      }
    }
  };

  useEffect(() => {     
    updatePracticeHTML();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitedUserData.groups]);

  // Build selected practice options with location_id as value
  const selectedPracticeOptions = (() => {
    const options = [];
    (userProfileData?.accounts || []).forEach((account) => {
      (account?.practices || []).forEach((practice) => {
        const practiceKey = `/${account?.practices[0]?.account_name}/${practice?.name}`;
        if (invitedUserData?.groups?.includes(practiceKey)) {
          options.push({ value: practice?.location_id, label: practice?.name });
        }
      });
    });
    return options;
  })();

  useEffect(() => {
    if (!invitedUserData?.default_location_id) return;
    const stillSelected = selectedPracticeOptions.some(
      (o) => o.value === invitedUserData.default_location_id
    );
    if (!stillSelected) {
      setInvitedUserData((prev) => ({ ...prev, default_location_id: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitedUserData.groups, userProfileData]);


  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={invitedUserData} onOpenChange={onClose}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4">
            <Drawer.Title className="sr-only">Edit User</Drawer.Title>
            <Drawer.Description className="sr-only">Edit a new user</Drawer.Description>
            <div className="docpro_modal_wrap docpro_modal_lg docpro_modal_lg2 center">
              <div className="docpro_modal">
                <button
                  className="docpro_close_btn"
                  onClick={onClose}
                >
                  <X />
                </button>
                <h2 className="docpro_fs_24">Edit User</h2>

                <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_3">
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      Last Name <span className="docpro_red_color">*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      defaultValue={invitedUserData?.last_name || ""}
                      className="docpro_form_field size_lg"
                      autoComplete="off"
                      onChange={(e) => {handleChange(e)}}
                      placeholder="Last Name"
                    />
                  </div>
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      First Name <span className="docpro_red_color">*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      defaultValue={invitedUserData?.first_name || ""}
                      className="docpro_form_field size_lg"
                      autoComplete="off"
                      onChange={(e) => {handleChange(e)}}
                      placeholder="First Name"
                    />
                  </div>
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      Email
                    </label>
                    <input
                      type="text"
                      defaultValue={invitedUserData?.recipient_email || ""}
                      className="docpro_form_field size_lg"
                      disabled
                    />
                  </div>
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">Contact</label>
                    <input 
                      type="text"
                      className="docpro_form_field size_lg us_phone"
                      name="contact"
                      autoComplete="off"
                      onChange={(e) => {handleChange(e)}}
                      defaultValue={invitedUserData?.contact ? usPhoneFormat(invitedUserData?.contact) : ""}
                      maxLength={20}
                      placeholder="(123) 123 1234"
                    />
                  </div>
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      Role <span className="docpro_red_color">*</span>
                    </label>
                    <DropdownWithSearch
                      placeholder={
                        (invitedUserData?.roles && invitedUserData?.roles?.length)
                          ? `${invitedUserData?.roles?.join(", ")}`
                          : "Select Roles"
                      }
                    >
                      <ul className="docpro_checkmark_list">
                        {(can_visit("administrator") ? userRoles : userRoles?.filter(
                          (role) => !["Trial", "System", "Enterprise"]?.includes(role)
                        ))?.map((item, index) => (
                          <li key={index}>
                            <div className="docpro_checkbox" style={{ top: "3px" }}>
                              <input
                                type="checkbox"
                                checked={invitedUserData?.roles?.includes(item)}
                                onChange={() => handleChangeRoles(item)}
                              />
                              <label>
                                <span style={{ marginLeft: "8px" }}>{item}</span>
                              </label>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </DropdownWithSearch>
                  </div>
                  
                  {allSpeciality?.length > 0 && (
                    <div className="docpro_grid_item">
                      <label className="docpro_fw_500 docpro_primary_color">
                        Specialities
                      </label>
                      <Select
                        isMulti
                        name="speciality_ids"
                        options={allSpeciality}
                        placeholder="Search..."
                        isSearchable
                        value={allSpeciality?.filter((option) => 
                          invitedUserData?.speciality_ids?.includes(option.value)
                        ) || []}
                        onChange={(selectedOption) => handleSpecialityChange(selectedOption)}
                        className="react-select-container w-full"
                        classNamePrefix="react-select"
                        filterOption={(option, rawInput) => {
                          const words = rawInput?.toLowerCase()?.split(" ");
                          return words?.every((word) =>
                            option?.label?.toLowerCase()?.includes(word)
                          );
                        }}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '40px',
                            borderColor: 'var(--border)',
                            borderRadius: '8px'
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1050,
                            marginTop: '-2px',
                            borderRadius: '0 0 3px 3px',
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1050,
                            padding: 0
                          }),
                        }}
                      />
                    </div>
                  )}
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      EMR Provider ID
                    </label>
                    <input
                      type="text"
                      name="emr_provider_id"
                      defaultValue={invitedUserData.emr_provider_id || ""}
                      className="docpro_form_field size_lg"
                      autoComplete="off"
                      onChange={(e) => {handleChange(e)}}
                      placeholder="Provider ID"
                    />
                  </div>
                  <div className="docpro_grid_item flex items-center">
                    <div className="flex items-center">
                      <label className="docpro_fw_500 docpro_primary_color docpro_mb_0 mr-2">
                        Are You a Provider?: 
                      </label>
                      <label className="switch switch_mode mb-0">
                        <input
                          name="is_provider"
                          checked={invitedUserData?.is_provider || false}
                          onChange={handleIsProviderChange}
                          type="checkbox"
                          className="docpro_logo_color"
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  </div>
                  <div className="docpro_grid_item">
                    <label className="docpro_fw_500 docpro_primary_color">
                      Default Location
                    </label>
                    <Select
                      name="default_location_id"
                      placeholder={selectedPracticeOptions?.length
                        ? "Select a default location"
                        : "Select practices first"
                      }
                      isSearchable
                      isClearable
                      options={selectedPracticeOptions}
                      value={invitedUserData?.default_location_id
                        ? selectedPracticeOptions.find(o => o.value === invitedUserData.default_location_id) || null
                        : null
                      }
                      onChange={(opt) => {
                        setInvitedUserData(prev => ({
                          ...prev,
                          default_location_id: opt?.value || ""
                        }));
                      }}
                      className="react-select-container w-full"
                      classNamePrefix="react-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '40px',
                          borderColor: 'var(--border)',
                          borderRadius: '8px'
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 1050,
                          marginTop: '-2px',
                          borderRadius: '0 0 3px 3px',
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 1050, padding: 0 }),
                      }}
                    />
                  </div>

                  {can_visit('ehr_veradigm') && (
                    <>
                      <div className="docpro_grid_item">
                        <label className="docpro_fw_500 docpro_primary_color"> EHR Type </label>
                        <Select
                          name="ehr_type"
                          options={ehrType}
                          onChange={(selectedOption) => {
                            setInvitedUserData({ ...invitedUserData, ehr_type: selectedOption.value });
                          }}
                          placeholder="Search..."
                          isSearchable={true}
                          value={ehrType.find(
                            (option) => option.value === invitedUserData.ehr_type
                          )}
                          className="react-select-container w-full"
                          classNamePrefix="react-select"
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: '40px',
                              borderRadius: '8px',
                              borderColor: 'var(--border)',
                            }),
                          }}
                        />
                      </div>
                      <div className="docpro_grid_item">
                        <label className="docpro_fw_500 docpro_primary_color"> EHR Username </label>
                        <input 
                          type="text"
                          className="docpro_form_field size_lg"
                          name="ehr_username"
                          autoComplete="off"
                          onChange={(e) => {handleChange(e)}}
                          defaultValue={invitedUserData.ehr_username}
                        />
                      </div>
                      <div className="docpro_grid_item">
                        <label className="docpro_fw_500 docpro_primary_color"> EHR Password </label>
                        <div className="docpro_form_field_with_icon">
                          <input
                            name="ehr_password"
                            type={ehrPassword ? "text" : "password"}
                            placeholder="••••••••"
                            className={`docpro_form_field size_lg`}
                            onChange={handleEhrPasswordChange}
                            autoComplete="new-password"
                            minLength={4}
                          />
                          <Lock width={18} height={18} className="docpro_form_field_icon" />
                          {ehrPassword ?
                            <Eye onClick={()=>setEhrPassword(false)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                          :
                            <EyeOff onClick={()=>setEhrPassword(true)} width={18} height={18} className="docpro_form_field_icon pointer" style={{ color: "#b0b0b0" }} />
                          }
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <Spacing lg="16" sm="16" />
                <div className="docpro_grid_item multiple_select">
                  <label className="docpro_fw_500 docpro_primary_color">
                    Practices <span className="docpro_red_color">*</span>
                  </label>
                  <DropdownWithSearch
                    placeholder={
                      invitedUserData?.groups?.length
                        ? `${invitedUserData?.groups?.join(", ")}`
                        : "Select Practices"
                    }
                  >
                    {userProfileData?.accounts?.map((account, index) => (
                      <div key={index}>
                        <li style={{ listStyle: "none", margin: "10px 0px" }}>
                          <div
                            className="docpro_checkbox"
                            style={{ top: "3px" }}
                          >
                            <input
                              type="checkbox"
                              onChange={() => handleSelectGroup(account)}
                              checked={invitedUserData?.groups?.includes(`/${account?.practices[0]?.account_name}`)}
                              // checked={invitedUserData?.groups?.some(group => group.includes(`/${account?.practices[0]?.account_name}`))}
                            />
                            <label>
                              <span style={{ marginLeft: "8px" }}>
                                {account?.practices[0]?.account_name}
                              </span>
                            </label>
                          </div>
                        </li>
                        {account?.practices?.map((practice) => {
                          const practiceKey = `/${account?.practices[0]?.account_name}/${practice?.name}`;
                          return (
                            <li
                              key={practice?.location_id}
                              style={{ listStyle: "none", paddingLeft: "15px" }}
                            >
                              <div
                                className="docpro_checkbox"
                                style={{ top: "3px" }}
                              >
                                <input
                                  type="checkbox"
                                  onChange={() => handleChangePractices(practice?.name, account?.practices[0]?.account_name, practice?.location_id)}
                                  checked={invitedUserData?.groups?.includes(practiceKey)}
                                />
                                <label>
                                  <span style={{ marginLeft: "8px" }}>
                                    {practice?.name}
                                  </span>
                                </label>
                              </div>
                            </li>
                          )
                        })}
                      </div>
                    ))}
                  </DropdownWithSearch>
                </div>

                {allProvidersOptions?.length > 0 && (
                  <>
                    <Spacing lg="16" sm="16" />
                    <div className="docpro_grid_item">
                      <label className="docpro_fw_500 docpro_primary_color">
                        Linked Providers
                      </label>
                      <Select
                        isMulti
                        name="linked_providers"
                        options={allProvidersOptions}
                        placeholder="Search..."
                        isSearchable
                        value={allProvidersOptions?.filter((option) => 
                          invitedUserData?.linked_providers?.includes(option?.value)
                        ) || []}
                        onChange={(selectedOption) => handleLinkedProvidersChange(selectedOption)}
                        className="react-select-container w-full"
                        classNamePrefix="react-select"                        
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '40px',
                            borderColor: 'var(--border)',
                            borderRadius: '8px'
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1050,
                            marginTop: '-2px',
                            borderRadius: '0 0 3px 3px',
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1050,
                            padding: 0
                          }),
                        }}
                      />
                    </div>
                  </>
                )}

                <Spacing lg="40" sm="40" />
                <div className="docpro_modal_btns">
                  <button
                    className="docpro_bordered_btn docpro_fw_600 docpro_filed_btn size_lg"
                    onClick={() =>
                      handleUpdatedInvitedUserInfo(
                        { ...invitedUserData },
                        () => { onClose(); }
                      )
                    }
                    style={{ minWidth: "170px" }}
                    disabled = {updateLoader ? true : false}
                  >
                    <Save width={20} height={20} />
                    <span>Update</span>
                    {updateLoader && (
                      <span className="docpro_loading">
                        <Loader width={20} height={20} />
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}
